import React, { useState, useEffect } from "react";
import Swal from "sweetalert2"; // ✅ Import SweetAlert2
import { CATEGORY_API } from "../../../data/api";

const Category = () => {
  const [openForm, setOpenForm] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    parentCategoryId: "",
    categoryName: "",
    description: "",
    isActive: true,
    imageURLs: [""]
  });

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(CATEGORY_API.GET_ALL);
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.log("Fetch Error:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "imageURLs") {
      setFormData({ ...formData, imageURLs: [value] });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      parentCategoryId: formData.parentCategoryId
        ? Number(formData.parentCategoryId)
        : null,
      categoryName: formData.categoryName,
      description: formData.description,
      isActive: formData.isActive === "true" || formData.isActive === true,
      imageURLs: formData.imageURLs
    };

    try {
      const res = await fetch(CATEGORY_API.CREATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Category Created!",
          text: "The category has been added successfully.",
          timer: 2000,
          showConfirmButton: false
        });

        fetchCategories();
        setOpenForm(false);
        setFormData({
          parentCategoryId: "",
          categoryName: "",
          description: "",
          isActive: true,
          imageURLs: [""]
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: "Unable to create category. Please try again."
        });
      }
    } catch (err) {
      console.log("Error:", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong. Please try again."
      });
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
          🎯 Manage Categories
        </h1>
        <button
          onClick={() => setOpenForm(!openForm)}
          className="bg-blue-600 hover:bg-blue-700 transition text-white px-5 py-2 rounded-lg shadow-md"
        >
          {openForm ? "Close Form" : "Add Category"}
        </button>
      </div>

      {/* Category Form */}
      {openForm && (
        <div className="bg-white p-6 shadow-xl rounded-xl border mb-6">
          <h2 className="text-2xl font-semibold mb-5 text-gray-700">
            Add New Category
          </h2>

          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
            onSubmit={handleSubmit}
          >
            {/* Category Name */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-600 mb-1">
                Category Name
              </label>
              <input
                name="categoryName"
                type="text"
                value={formData.categoryName}
                onChange={handleChange}
                className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            {/* Parent Category */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-600 mb-1">
                Parent Category
              </label>
              <select
                name="parentCategoryId"
                value={formData.parentCategoryId}
                onChange={handleChange}
                className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
              >
                <option value="">None</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="flex flex-col md:col-span-2">
              <label className="font-medium text-gray-600 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none resize-none h-24"
              />
            </div>

            {/* Image URL */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-600 mb-1">Image URL</label>
              <input
                name="imageURLs"
                type="text"
                value={formData.imageURLs[0]}
                onChange={handleChange}
                className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                placeholder="image-name.png"
              />
            </div>

            {/* Status */}
            <div className="flex flex-col">
              <label className="font-medium text-gray-600 mb-1">Status</label>
              <select
                name="isActive"
                value={formData.isActive}
                onChange={handleChange}
                className="border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
            </div>

            {/* Submit */}
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 transition text-white px-6 py-2 rounded-lg shadow-md"
              >
                Add Category
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category List */}
      <h2 className="text-2xl font-semibold mb-4 text-gray-700">
        All Categories
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {categories.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center">
            No categories found...
          </p>
        ) : (
          categories.map((cat, index) => (
            <div
              key={index}
              className="bg-white p-5 rounded-xl shadow-lg border"
            >
              {cat.imageURLs?.[0] && (
                <img
                  src={cat.imageURLs[0]}
                  alt="category"
                  className="h-32 w-full object-cover rounded-lg mb-3"
                />
              )}
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                {cat.categoryName}
              </h3>
              <p className="text-gray-500 mb-1">
                Parent: {cat.parentCategoryId || "None"}
              </p>
              <p className="text-gray-500 mb-2">{cat.description}</p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  cat.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {cat.isActive ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Category;
